import React from 'react';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { addYears, startOfToday, endOfDay } from 'date-fns';
import { TextAreaGroup, NumberStepper } from 'cstar-react-primitives/lib/formik';

import { DenomPicker } from '../../denomPicker/DenomPicker';
import { giftCardFieldMessages } from './formFieldMessages';
import { cashbotName } from '../../core';
import { useFormatMessage } from '../../../intl/intlHooks';
import { DeliveryDatePicker } from '../../datePicker/DeliveryDatePicker';
import { LastNameFormikField, EmailFormikField, FullNameFormikField } from '../../address/AddressFormikFields';

export const AMOUNT_FIELD = 'amount';
export const BRAND_CODE = 'brandCode';
export const IS_SELF_BUY = 'isSelfBuy';
export const MESSAGE_FIELD = 'message';
export const QUANTITY_FIELD = 'quantity';
export const EXPECT_A_GIFT = 'expectAGift';
export const SENDER_NAME_FIELD = 'senderName';
export const FACEPLATE_CODE = 'faceplateCode';
export const FACEPLATE_NAME = 'faceplateName';
export const SENDER_EMAIL_FIELD = 'senderEmail';
export const FACEPLATE_IMAGE = 'faceplateImage';
export const SHIPPING_AMOUNT = 'shippingAmount';
export const ADDRESS_HASH_KEY = 'addressHashKey';
export const IS_SAVED_ADDRESS = 'isSavedAddress';
export const DELIVERY_DATE_FIELD = 'deliveryDate';
export const RECIPIENT_NAME_FIELD = 'recipientName';
export const RECIPIENT_EMAIL_FIELD = 'recipientEmail';
export const DELIVERY_METHOD_FIELD = 'deliveryMethod';
export const SHIPPING_METHOD_TOKEN = 'shippingMethodToken';
export const NAME = 'name';

export const MESSAGE_MAX_LENGTH = 250;
export const LINE_ITEM_MAX_QUANTITY = 99;

export const messageCounterAriaLabel = formatMessage => (current, maxLength) =>
    formatMessage(giftCardFieldMessages.messageCounterAria, { current, maxLength });

export const currencyFormat = (value, formatNumber) =>
    formatNumber(value, { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

export const SenderNameField = () => {
    const formatMessage = useFormatMessage();
    return (
        <LastNameFormikField
            fieldName={SENDER_NAME_FIELD}
            label={formatMessage(giftCardFieldMessages.senderNameFieldLabel)}
        />
    );
};
export const RecipientNameField = () => {
    const formatMessage = useFormatMessage();
    return (
        <FullNameFormikField
            fieldName={RECIPIENT_NAME_FIELD}
            label={formatMessage(giftCardFieldMessages.recipientNameFieldLabel)}
        />
    );
};
export const RecipientEmailField = () => {
    const formatMessage = useFormatMessage();
    return (
        <EmailFormikField
            fieldName={RECIPIENT_EMAIL_FIELD}
            label={formatMessage(giftCardFieldMessages.recipientEmailFieldLabel)}
        />
    );
};
export const MessageField = ({ maximumLength }) => {
    const formatMessage = useFormatMessage();
    return (
        <Field
            name={MESSAGE_FIELD}
            label={formatMessage(giftCardFieldMessages.messageFieldLabel)}
            component={TextAreaGroup}
            counterAriaLabel={messageCounterAriaLabel(formatMessage)}
            atts={{ maxLength: maximumLength }}
            cashbotName={cashbotName}
        />
    );
};
MessageField.propTypes = {
    maximumLength: PropTypes.number
};
MessageField.defaultProps = {
    maximumLength: MESSAGE_MAX_LENGTH
};
export const AmountField = ({ minValue, maxValue, incValue }) => {
    const formatMessage = useFormatMessage();
    return (
        <Field
            name={AMOUNT_FIELD}
            atts={{ maxLength: 7 }}
            helper={formatMessage(giftCardFieldMessages.amountRange, {
                min: parseFloat(minValue).toFixed(2),
                max: parseFloat(maxValue).toFixed(2)
            })}
            incLabel={formatMessage(giftCardFieldMessages.amountIncrement)}
            decLabel={formatMessage(giftCardFieldMessages.amountDecrement)}
            incValue={incValue}
            component={NumberStepper}
            format={val => val}
            usePlusMinus
            isRequiredField
            cashbotName={cashbotName}
            minValue={minValue}
            maxValue={maxValue}
        />
    );
};
AmountField.defaultProps = {
    incValue: 5
};
AmountField.propTypes = {
    incValue: PropTypes.number,
    minValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    maxValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
export const DenominationField = ({ setFieldValue, denominations, value }) => {
    const formatMessage = useFormatMessage();
    return (
        <Field
            name={AMOUNT_FIELD}
            legend={formatMessage(giftCardFieldMessages.denominationLabel)}
            handleChange={val => setFieldValue(AMOUNT_FIELD, val)}
            denominations={denominations}
            value={value}
            formatValue={val => val}
            cashbotName={cashbotName}
            component={DenomPicker}
            isRequiredField
        />
    );
};
DenominationField.propTypes = {
    setFieldValue: PropTypes.func.isRequired,
    denominations: PropTypes.array.isRequired,
    value: PropTypes.string.isRequired
};

export const DeliveryDateField = ({ setFieldValue, dateValue, error }) => {
    const formatMessage = useFormatMessage();
    const setUTC = (date) => {
        if (typeof date !== 'undefined') {
            
            date.setUTCHours(12);
        }
        setFieldValue(DELIVERY_DATE_FIELD, date);
    };
    return (
        <Field
            label={formatMessage(giftCardFieldMessages.deliveryDateFieldLabel)}
            name={DELIVERY_DATE_FIELD}
            setDeliveryDateFieldValue={date => setUTC(date)}
            selected={new Date(dateValue)}
            minDate={startOfToday()}
            maxDate={endOfDay(addYears(new Date(), 1))}
            error={error}
            component={DeliveryDatePicker}
        />
    );
};

DeliveryDateField.defaultProps = {
    error: ''
};
DeliveryDateField.propTypes = {
    dateValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    setFieldValue: PropTypes.func.isRequired,
    error: PropTypes.string
};
